import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  buildLearnerNavigationState,
  getLearnerNavigationState,
  navigateLearnerBack,
} from "src/pages/coaching/utils/manageNavigation";
import { useSelector } from "react-redux";
import { Devices, LearningRecordSimulation, SelectedDevices } from "src/types/coaching/learners";
import React, { useCallback, useEffect, useMemo, useState } from "react";

import _ from 'lodash';

import styles from "./view.module.scss";
import { useDispatch } from "react-redux";

import {
  completeSimulation,
  fetchLearningRecordSimulation,
  fetchSimulationReport,
  fetchTranscriptsForLearningSimulation,
  invalidateCompleteSimulation,
  invalidateRestartSimulation,
  invalidateRetakeSimulation,
  invalidateSimulationReport,
  invalidateStartSimulation,
  restartSimulation,
  retakeSimulation,
  startSimulation
} from "src/redux/actions/coaching/learners";

import { SimulationSummaryComponent } from "src/pages/coaching/components/simulation/view";
import { SimulationContext, SimulationContextProps } from "src/pages/coaching/components/simulation/view/context";

import userRoleService from "src/utils/userRoles.service";
import PermissionDenined from "src/components/permissionDenied";
import { SimulationNotFound } from "src/pages/coaching/components/notFound";

export const ViewSimulationPage: React.FC<object> = () => {
  const dispatch = useDispatch();
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const learnerNavigation = useMemo(
    () => getLearnerNavigationState(location.state),
    [location.state],
  );

  const simulationId = params.id;

  const simulation = useSelector((state: any) =>
    _.get(state.learners, `simulation[${simulationId}].item`) as LearningRecordSimulation,
  );

  const simulationErrorCode = useSelector((state: any) => {
    return _.get(state.learners, `simulation[${simulationId}].error.errors[0].code`);
  });

  const loading = useSelector((state: any) =>
    _.get(state.learners, `simulation[${simulationId}].loading`),
  );

  const startingSimulation = useSelector((state: any) => {
    const starting = _.get(state.learners, `simulation[${simulationId}].start.starting`);
    return starting;
  });

  const simulationStarted = useSelector((state: any) => {
    return _.get(state.learners, `simulation[${simulationId}].start.started`);
  });

  const restartingSimulation = useSelector((state: any) => {
    const starting = _.get(state.learners, `simulation[${simulationId}].restart.restarting`);
    return starting;
  });

  const simulationRestarted = useSelector((state: any) => {
    return _.get(state.learners, `simulation[${simulationId}].restart.restarted`);
  });

  const completingSimulation = useSelector((state: any) => {
    return _.get(state.learners, `simulation[${simulationId}].complete.completing`);
  });

  const simulationCompleted = useSelector((state: any) => {
    return _.get(state.learners, `simulation[${simulationId}].complete.completed`);
  });

  const fetchingSimulationReport = useSelector((state: any) => {
    const fetching = _.get(state.learners, `simulation[${simulationId}].report.fetching`);
    return fetching;
  });

  const [selectedAttemptId, setSelectedAttemptId] = React.useState<string | null>(null);
  const activeId = selectedAttemptId || simulationId;
  

  const fetchedSimulationReport = useSelector((state: any) => {
    const fetched = _.get(state.learners, `simulation[${activeId}].report.fetched`);
    return fetched;
  });

const simulationAssessmentReport = useSelector((state: any) => {
  return _.get(state.learners, `simulation[${activeId}].item.report`);
});

  const fetchingSimulationTranscripts = useSelector((state: any) => {
    const fetching = _.get(state.learners, `simulation[${activeId}].transcripts.fetching`);
    return fetching;
  });

  const fetchedSimulationTranscripts = useSelector((state: any) => {
    const fetched = _.get(state.learners, `simulation[${activeId}].transcripts.fetched`);
    return fetched;
  });

  const transcripts = useSelector((state: any) => {
    return _.get(state.learners, `simulation[${activeId}].transcripts.item`);
  });

  const retakingSimulation = useSelector((state: any) => {
    return _.get(state.learners, `simulation[${simulationId}].retake.retaking`);
  });  

  const simulationRetaken = useSelector((state: any) => {
    return _.get(state.learners, `simulation[${simulationId}].retake.retaken`);
  });

  const simulationRetakenId = useSelector((state: any) => {
    return _.get(state.learners, `simulation[${simulationId}].retake.id`);
  });  

  const [mediaDevices, setMediaDevices] = useState<Devices>({
    audioInputs: [],
    audioOutputs: [],
    videoInputs: [],
  });

  const [selectedDeviceIds, setSelectedDeviceIds] = useState<SelectedDevices>({
    audioInput: "",
    audioOutput: "",
    videoInput: "",
  });

  const handleSetDevices = useCallback((next: Devices) => {
    setMediaDevices(next);
  }, []);

  const handleDeviceChange = useCallback(
    (kind: "audioInput" | "audioOutput" | "videoInput", deviceId: string) => {
      setSelectedDeviceIds((previous) => ({ ...previous, [kind]: deviceId }));
    },
    [],
  );

  const [courseContext, setCourseContext] = React.useState<SimulationContextProps>({
    id: simulationId,
    view: "summary",
    showLearnerAttemptsDropdown: true,
  });

  useEffect(() => {
    setCourseContext((prev) => ({
      ...prev,
      devices: mediaDevices,
      selectedDevices: selectedDeviceIds,
      setDevices: handleSetDevices,
      onDeviceChange: handleDeviceChange,
    }));
  }, [mediaDevices, selectedDeviceIds, handleSetDevices, handleDeviceChange]);

  const handleChangeView = (view: any) => {
    setCourseContext((prev) => {
      return {
        ...prev,
        view: view,
        secondaryView: null,
      }
    });
  }

  const handleSecondaryChangeView = (view: any) => {
    setCourseContext((prev) => {
      return {
        ...prev,
        secondaryView: view,
      }
    });
  }

  const handleBackClicked = () => {
    navigateLearnerBack(navigate, learnerNavigation);
  };

  const handleStartCourse = () => {
    dispatch(startSimulation(simulation.id));
  }

  const handleRestartCourse = () => {
    dispatch(restartSimulation(simulation.id));
  }

  const handleEndCourse = (simulation: LearningRecordSimulation) => {
    dispatch(completeSimulation({
      id: simulation.id!,
      session: simulation.session!,
    }));
  };
  
  const handleViewReport = (attemptId?: string) => {
  const id = attemptId || simulation.id;
  setSelectedAttemptId(id);
  dispatch(fetchSimulationReport({ id }));
  dispatch(fetchTranscriptsForLearningSimulation(id));
  };

  const handleFetchTranscripts = () => {
    dispatch(fetchTranscriptsForLearningSimulation(selectedAttemptId || simulation.id!));
  }

  const handleRetakeSimulation = () => {
    dispatch(retakeSimulation(simulation.id));
  }

  useEffect(() => {
    if (simulationId) {
      dispatch(fetchLearningRecordSimulation(simulationId));
    }

    setCourseContext((prev) => {
      return {
        ...prev,
        id: simulationId,
        view: "summary",
      }
    });
  }, [simulationId]);

  useEffect(() => {
    setCourseContext((prev) => {
      const currentView =
        prev.view === "report" ||
        prev.secondaryView === "call-recording" ||
        prev.secondaryView === "call-recording-minimized";
        
      const simulationView = simulation?.status === "IN_PROGRESS" ? "simulator" : "summary";

      return {
        ...prev,
        view: currentView ? prev.view : simulationView,
        secondaryView: currentView ? prev.secondaryView : null,
        onBack: handleBackClicked,
        startSimulation: handleStartCourse,
        endSimulation: handleEndCourse,
        restartSimulation: handleRestartCourse,
        retakeSimulation: handleRetakeSimulation,
        viewReport: handleViewReport,
        fetchTranscripts: handleFetchTranscripts,
        changeView: handleChangeView,
        changeSecondaryView: handleSecondaryChangeView,
      }
    });
  }, [simulation]);

  useEffect(() => {
    setCourseContext((prev) => {
      return {
        ...prev,
        startingSimulation: startingSimulation
      }
    });
  }, [startingSimulation]);

  useEffect(() => {
    if (simulationStarted) {
      handleChangeView("simulator");
      dispatch(invalidateStartSimulation(simulation.id));
    }
  }, [simulationStarted]);

  useEffect(() => {
    setCourseContext((prev) => {
      return {
        ...prev,
        restartingSimulation: restartingSimulation
      }
    });
  }, [restartingSimulation]);

  useEffect(() => {
    if (simulationRestarted) {
      handleChangeView("simulator");
      dispatch(invalidateRestartSimulation(simulation.id));
    }
  }, [simulationRestarted]);

  useEffect(() => {
    setCourseContext((prev) => {
      return {
        ...prev,
        endingSimulation: completingSimulation
      }
    });
  }, [completingSimulation]);

  useEffect(() => {
    if (simulationCompleted) {
      const status = _.get(simulation, "report.status");
      if (!simulation.report || status === "in_sufficient_duration") {
        handleChangeView("failed");
      } else {
        handleChangeView("completion");
      }
      dispatch(invalidateCompleteSimulation(simulation.id));
    }
  }, [simulationCompleted]);

  useEffect(() => {
    setCourseContext((prev) => {
      return {
        ...prev,
        fetchingReport: fetchingSimulationReport,
        fetchedReport: fetchedSimulationReport,
        assessmentReport: simulationAssessmentReport,
      }
    });

    if (fetchedSimulationReport) {
      handleChangeView("report");
    }

    if (simulation) {
      dispatch(invalidateSimulationReport(simulation.id));
    }

  }, [fetchingSimulationReport, fetchedSimulationReport, simulationAssessmentReport]);

  useEffect(() => {
    setCourseContext((prev) => {
      return {
        ...prev,
        fetchingTranscripts: fetchingSimulationTranscripts,
        fetchedTranscripts: fetchedSimulationTranscripts,
        transcripts: transcripts
      }
    });
  }, [fetchingSimulationTranscripts, fetchedSimulationTranscripts, transcripts]);

  useEffect(() => {
    setCourseContext((prev) => ({
      ...prev,
      selectedAttemptId: activeId ?? undefined,
    }));
  }, [activeId]);

  useEffect(() => {
    setCourseContext((prev) => {
      return {
        ...prev,
        retakingSimulation: retakingSimulation,
      }
    });
  }, [retakingSimulation]);

  useEffect(() => {
    if (simulationRetaken && simulationRetakenId) {
      navigate(`/coaching/simulations/${simulationRetakenId}`, {
        replace: true,
        state: buildLearnerNavigationState(learnerNavigation),
      });
      handleChangeView("simulator");
      dispatch(invalidateRetakeSimulation(simulationRetakenId));
    }
  }, [simulationRetaken]);

  if (!userRoleService.canAccessCoaching()) {
    return <PermissionDenined className={styles.permissionDenied} />;
  }

  if (simulationErrorCode === 10007) {
    return <SimulationNotFound onBack={() => {
      navigateLearnerBack(navigate, learnerNavigation);
    }}/>;
  }

  if ((loading && simulation?.id != simulationId) || !simulation) {
    return <div className={styles.loading}>Loading simulation...</div>
  }

  return (
    <SimulationContext.Provider value={courseContext}>
      <SimulationSummaryComponent simulation={simulation} />
    </SimulationContext.Provider>
  );
}